import React, { useMemo } from 'react'
import { GuildOffer } from 'interface/guildOffersInterface'
import { Offer } from 'interface/offerInterface'
import { useConnectWalletButtonStates } from '@shared/hooks/use-connect-wallet-button-states'
import { useTranslation } from '../../i18n/useTranslation'
import { Loader } from '../loader'

interface PurchaseButtonProps {
  isWalletConnected: boolean
  transactionPending: boolean
  onPurchase: () => void
  isInsufficientBalance?: boolean
  isWalletMatched: boolean
  isLimitReached?: boolean
  isSoldOut?: boolean
  offer: GuildOffer | Offer
  selectedQuantity: number
  selectedMembers: string[]
  isAccountLoading: boolean
}

export const PurchaseButton: React.FC<PurchaseButtonProps> = ({
  isWalletConnected,
  transactionPending,
  onPurchase,
  isInsufficientBalance,
  isWalletMatched,
  isSoldOut,
  isLimitReached,
  offer,
  selectedQuantity,
  selectedMembers,
  isAccountLoading
}) => {
  const { offerCardTranslations, offerCheckoutTranslations } = useTranslation()
  const { buttonText, buttonAction } = useConnectWalletButtonStates()

  const exceedsLimit = useMemo(() => {
    if (selectedMembers.length === 0 || !('guildMemberSupplies' in offer)) {
      return false
    }

    return selectedMembers.some(memberId => {
      const remainingSupply =
        offer.guildMemberSupplies[memberId] ?? offer.maxMemberSupply
      return (selectedQuantity || 1) > remainingSupply
    })
  }, [selectedMembers, selectedQuantity, offer])

  const noMembersSelected = useMemo(() => {
    if (!('guildMemberSupplies' in offer)) {
      return false
    }
    return selectedMembers.length === 0
  }, [selectedMembers, offer])

  if (isWalletMatched && isWalletConnected) {
    if (isLimitReached || isSoldOut) {
      return (
        <button
          type='button'
          className='green-button-button font-border poppins-2xl h-[66px] w-full hover:brightness-125'
          disabled
        >
          {isLimitReached
            ? offerCardTranslations.limitReached
            : offerCardTranslations.soldOut}
        </button>
      )
    }

    if (isInsufficientBalance && !isAccountLoading) {
      return (
        <button
          type='button'
          className='red-button font-border poppins-2xl h-[66px] w-full'
        >
          {offerCheckoutTranslations.insufficientBalance}
        </button>
      )
    }

    return (
      <button
        type='button'
        disabled={
          !isWalletConnected ||
          ('guildMemberSupplies' in offer && !exceedsLimit) ||
          noMembersSelected ||
          isAccountLoading
        }
        onClick={() => !transactionPending && onPurchase()}
        className='green-button font-border poppins-2xl h-[66px] w-full hover:brightness-125'
      >
        {transactionPending || isAccountLoading ? (
          <Loader
            width={48}
            height={48}
          />
        ) : (
          offerCheckoutTranslations.confirmPurchase
        )}
      </button>
    )
  }

  return (
    <button
      type='button'
      className='green-button font-border poppins-2xl h-[66px] w-full uppercase hover:brightness-125'
      onClick={buttonAction}
    >
      {buttonText}
    </button>
  )
}
